// PostsGrid.tsx
"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import SafeHtml from "@/components/ui/SafeHtml";
import { toAbsoluteUrl } from "@/lib/toAbsoluteUrl";
import { fetchPostsGrid } from "@/lib/fetchPostsGrid";
import { getLocaleFromLang } from "@/lib/getLocaleFromLang";

import type { PostsGridFields } from "@/types/acf";
import type { WPPost } from "@/types/wpBase";

function pageWindow(current: number, total: number, size = 5) {
  if (total <= size) return Array.from({ length: total }, (_, i) => i + 1);
  const half = Math.floor(size / 2);

  let start = current - half;
  let end = current + half;

  if (start < 1) {
    start = 1;
    end = size;
  }
  if (end > total) {
    end = total;
    start = total - size + 1;
  }

  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

function formatDate(date?: string, lang?: string) {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";

  return new Intl.DateTimeFormat(getLocaleFromLang(lang), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null;
}

function getWpFeaturedImage(post: unknown): { src: string; alt: string } | null {
  if (!isRecord(post)) return null;
  if (!post["featured_media"]) return null;

  const embedded = post["_embedded"];
  if (!isRecord(embedded)) return null;

  const featuredMedia = embedded["wp:featuredmedia"];
  if (!Array.isArray(featuredMedia) || featuredMedia.length === 0) return null;

  const fm = featuredMedia[0];
  if (!isRecord(fm)) return null;

  const mediaDetails = fm["media_details"];
  const sizes = isRecord(mediaDetails) ? mediaDetails["sizes"] : undefined;

  const large = isRecord(sizes) ? sizes["large"] : undefined;
  const mediumLarge = isRecord(sizes) ? sizes["medium_large"] : undefined;

  const largeUrl =
    isRecord(large) && typeof large["source_url"] === "string" ? large["source_url"] : undefined;

  const mediumLargeUrl =
    isRecord(mediumLarge) && typeof mediumLarge["source_url"] === "string"
      ? mediumLarge["source_url"]
      : undefined;

  const sourceUrl = typeof fm["source_url"] === "string" ? fm["source_url"] : undefined;

  const url = largeUrl || mediumLargeUrl || sourceUrl;

  const abs = url ? toAbsoluteUrl(url) : null;
  if (!abs) return null;

  const alt = typeof fm["alt_text"] === "string" ? fm["alt_text"] : "";

  return { src: abs, alt };
}

function Skeleton({ className }: { className: string }) {
  return <div className={`animate-pulse bg-black/10 ${className}`} />;
}

function FeaturedSkeleton() {
  return (
    <div className="relative aspect-square w-full overflow-hidden">
      <Skeleton className="absolute inset-0" />
      <div className="absolute inset-0 flex flex-col justify-end p-6">
        <Skeleton className="h-5 w-4/5" />
        <Skeleton className="h-4 w-40 mt-3" />
      </div>
    </div>
  );
}

function GridSkeleton() {
  return (
    <div className="w-full">
      <div className="relative w-full aspect-[3/2] overflow-hidden">
        <Skeleton className="absolute inset-0" />
      </div>
      <div className="mt-3">
        <Skeleton className="h-4 w-36" />
        <Skeleton className="h-5 w-5/6 mt-2" />
      </div>
    </div>
  );
}

function FeaturedCard({ post, lang }: { post: WPPost; lang?: string }) {
  const href = post.link || "#";
  const image = getWpFeaturedImage(post);
  const date = formatDate(post.date, lang);
  const title = post.title?.rendered || "";

  return (
    <Link
      href={href}
      className="group relative block aspect-square w-full overflow-hidden no-underline hover:no-underline"
      aria-label={title ? undefined : "Open post"}
    >
      {image?.src ? (
        <Image
          src={image.src}
          alt={image.alt}
          fill
          className="object-cover transition-transform duration-200 ease-out group-hover:scale-[1.025]"
          sizes="(min-width: 768px) 50vw, 100vw"
          priority={false}
        />
      ) : null}

      <div className="absolute inset-0 flex flex-col justify-end p-6 text-white bg-gradient-to-t from-black/90 via-black/20 to-transparent">
        {title ? (
          <h3 className="mt-1 text-title-2 leading-snug">
            <SafeHtml content={title} />
          </h3>
        ) : null}
        {date ? <div className="mt-2">{date}</div> : null}
      </div>
    </Link>
  );
}

function GridCard({ post, lang }: { post: WPPost; lang?: string }) {
  const href = toAbsoluteUrl(post.link) ?? post.link ?? "#";
  const image = getWpFeaturedImage(post);
  const date = formatDate(post.date, lang);
  const title = post.title?.rendered || "";

  return (
    <Link href={href} className="group block w-full no-underline hover:no-underline">
      {image?.src ? (
        <div className="relative w-full aspect-[3/2] overflow-hidden">
          <Image
            src={image.src}
            alt={image.alt}
            fill
            className="object-cover transition-transform duration-200 ease-out group-hover:scale-[1.025]"
            sizes="(min-width: 768px) 33vw, 100vw"
            priority={false}
          />
        </div>
      ) : null}

      <div className="mt-3">
        {date ? <div className="font-regular">{date}</div> : null}
        {title ? (
          <h3 className="mt-1 text-title-4">
            <SafeHtml content={title} />
          </h3>
        ) : null}
      </div>
    </Link>
  );
}

type GridCacheValue = { posts: WPPost[] };
const gridCache = new Map<string, GridCacheValue>();

export default function PostsGrid({ data, lang }: { data: PostsGridFields; lang?: string }) {
  const isHidden = !data || !!data.hide;

  const FEATURED_COUNT = 2;
  const GRID_PER_PAGE = 15;

  const langCode = lang;

  const [page, setPage] = React.useState(1);
  const [featured, setFeatured] = React.useState<WPPost[]>([]);
  const [grid, setGrid] = React.useState<WPPost[]>([]);
  const [total, setTotal] = React.useState(0);

  const [isFeaturedLoading, setIsFeaturedLoading] = React.useState(true);
  const [isGridLoading, setIsGridLoading] = React.useState(true);

  const gridTopRef = React.useRef<HTMLDivElement | null>(null);

  const scrollToGridWithOffset = React.useCallback(() => {
    const el = gridTopRef.current;
    if (!el) return;

    const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize || "16");
    const offset = 66 + 2 * rootFont;

    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);

  const shouldScrollRef = React.useRef(false);

  React.useEffect(() => {
    if (!shouldScrollRef.current) return;
    shouldScrollRef.current = false;
    scrollToGridWithOffset();
  }, [page, scrollToGridWithOffset]);

  const cacheKey = React.useCallback(
    (p: number) => `${langCode || ""}:grid:${p}`,
    [langCode]
  );

  const prefetchGrid = React.useCallback(
    async (targetPage: number, totalPages: number) => {
      if (targetPage < 1 || targetPage > totalPages) return;

      const key = cacheKey(targetPage);
      if (gridCache.has(key)) return;

      const offset = FEATURED_COUNT + (targetPage - 1) * GRID_PER_PAGE;
      const res = await fetchPostsGrid(1, GRID_PER_PAGE, langCode, { offset });
      gridCache.set(key, { posts: res.posts || [] });
    },
    [cacheKey, langCode]
  );

  const totalGrid = Math.max(0, total - FEATURED_COUNT);
  const totalPages = Math.max(1, Math.ceil(totalGrid / GRID_PER_PAGE));
  const current = Math.max(1, Math.min(page, totalPages));
  const pages = totalPages > 1 ? pageWindow(current, totalPages, 5) : [];

  const goToPage = React.useCallback(
    (p: number) => {
      const next = Math.max(1, Math.min(p, totalPages));
      shouldScrollRef.current = true;

      if (next === page) {
        scrollToGridWithOffset();
        shouldScrollRef.current = false;
        return;
      }

      setPage(next);
    },
    [page, totalPages, scrollToGridWithOffset]
  );

  React.useEffect(() => {
    if (isHidden) return;
    setPage(1);
  }, [langCode, isHidden]);

  React.useEffect(() => {
    if (isHidden) return;

    let alive = true;
    setIsFeaturedLoading(true);

    fetchPostsGrid(1, FEATURED_COUNT, langCode)
      .then((r) => {
        if (!alive) return;
        setFeatured(r.posts || []);
        setTotal(r.total || 0);
      })
      .finally(() => {
        if (!alive) return;
        setIsFeaturedLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [langCode, isHidden]);

  React.useEffect(() => {
    if (isHidden) return;

    let alive = true;

    const key = cacheKey(page);
    const cached = gridCache.get(key);

    if (cached) {
      setGrid(cached.posts);
      setIsGridLoading(false);
      return () => {
        alive = false;
      };
    }

    setIsGridLoading(true);

    const offset = FEATURED_COUNT + (page - 1) * GRID_PER_PAGE;
    fetchPostsGrid(1, GRID_PER_PAGE, langCode, { offset })
      .then((r) => {
        if (!alive) return;
        const posts = r.posts || [];
        setGrid(posts);
        gridCache.set(key, { posts });
        if (!total && r.total) setTotal(r.total);
      })
      .finally(() => {
        if (!alive) return;
        setIsGridLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [page, langCode, cacheKey, total, isHidden]);

  React.useEffect(() => {
    if (isHidden) return;
    if (totalPages <= 1) return;

    void prefetchGrid(current + 1, totalPages);
    void prefetchGrid(current - 1, totalPages);
  }, [current, totalPages, prefetchGrid, isHidden]);

  if (isHidden) return null;

  return (
    <div id={data.id}>
      {data.title ? (
        <h2 className="text-title-1 text-center">
          <SafeHtml content={data.title} />
        </h2>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {isFeaturedLoading
          ? Array.from({ length: FEATURED_COUNT }).map((_, i) => (
              <FeaturedSkeleton key={`featured-skel-${i}`} />
            ))
          : featured.map((p) => <FeaturedCard key={p.id} post={p} lang={langCode} />)}
      </div>

      <div ref={gridTopRef} />

      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        {isGridLoading
          ? Array.from({ length: GRID_PER_PAGE }).map((_, i) => (
              <GridSkeleton key={`grid-skel-${i}`} />
            ))
          : grid.map((p) => <GridCard key={p.id} post={p} lang={langCode} />)}
      </div>

      {totalPages > 1 ? (
        <nav className="mt-12 flex flex-wrap items-center justify-center gap-6" aria-label="Pagination">
          <button
            type="button"
            disabled={current <= 1}
            onMouseEnter={() => void prefetchGrid(current - 1, totalPages)}
            onFocus={() => void prefetchGrid(current - 1, totalPages)}
            onClick={() => goToPage(current - 1)}
            className="px-2 py-1 disabled:opacity-40"
          >
            Prev
          </button>

          {pages.map((p) => {
            const isCurrent = p === current;
            return (
              <button
                key={p}
                type="button"
                onMouseEnter={() => void prefetchGrid(p, totalPages)}
                onFocus={() => void prefetchGrid(p, totalPages)}
                onClick={() => goToPage(p)}
                aria-current={isCurrent ? "page" : undefined}
                className={`px-2 py-1 ${isCurrent ? "underline underline-offset-4" : ""}`}
              >
                {p}
              </button>
            );
          })}

          <button
            type="button"
            disabled={current >= totalPages}
            onMouseEnter={() => void prefetchGrid(current + 1, totalPages)}
            onFocus={() => void prefetchGrid(current + 1, totalPages)}
            onClick={() => goToPage(current + 1)}
            className="px-2 py-1 disabled:opacity-40"
          >
            Next
          </button>
        </nav>
      ) : null}
    </div>
  );
}
